<script setup lang="ts">
import type { AppSvgIconType } from '../appSvgIcon/AppSvgIcon.model';
import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';

import type { AppLinkButtonSize } from './AppLinkButton.model';

interface AppLinkButtonProps {
  text: string;
  size: AppLinkButtonSize;
  href?: string;
  rightIcon?: AppSvgIconType;
  leftIcon?: AppSvgIconType;
}

const props = defineProps<AppLinkButtonProps>();

interface AppLinkButtonEmits {
  (eventName: 'click'): void;
}

const emit = defineEmits<AppLinkButtonEmits>();
</script>

<template>
  <a
    v-if="props.href"
    :href="props.href"
    target="_blank"
    rel="noopener noreferrer"
    :class="{
      'app-link': true,
      [props.size]: true,
    }"
  >
    {{ props.text }}
  </a>

  <button
    v-else
    :class="{
      'app-link-button': true,
      [props.size]: true,
      'has-icon': props.rightIcon || props.leftIcon,
    }"
    @click.stop="emit('click')"
  >
    <AppSvgIcon v-if="props.leftIcon" :name="props.leftIcon" />
    {{ props.text }}
    <AppSvgIcon v-if="props.rightIcon" :name="props.rightIcon" />
  </button>
</template>

<style scoped lang="scss">
@import '../../styles/_fonts.scss';
.app-link,
.app-link-button {
  background-color: transparent;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  text-decoration: underline;
  color: var(--color-secondary-40);
  text-align: left;

  &.secondary {
    color: var(--color-secondary-60);
  }

  &.small {
    @include font-link-s;
  }

  &.small-bold {
    @include font-link-s-bold;
  }

  &.large {
    @include font-link-l;
  }

  &.large-bold {
    @include font-label-l-bold;
  }

  &.has-icon {
    display: flex;
    gap: var(--spacing-8);
  }
}
</style>
